import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { MarqueeImage } from "../components/MarqueeImage";
import { RichText } from "../components/RichText";
import { useT } from "../lib/LanguageContext";

const MONACO_SUNSET =
  "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/4fpvbwk9_2.png";
const GEOMETRIC =
  "https://images.pexels.com/photos/36883116/pexels-photo-36883116.jpeg?auto=compress&cs=tinysrgb&w=1600";
const MONACO_AERIAL =
  "https://images.pexels.com/photos/11892592/pexels-photo-11892592.jpeg?auto=compress&cs=tinysrgb&w=1600";
const BAND2 = [
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/af62e6a888fecd48_IMG_2800.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/63ef33cd3d207146_6280C133-58E9-4737-8A46-022E55547348.PNG",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/2b838fd32a5c8b78_IMG_2755.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/45b29b0de085ece6_IMG_2616.webp",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/bd53eb5620c6da35_017205EA-3078-4063-BC80-8E17402627C2.jpg",
];
const BAND3 = [
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/acbdcce8616f0a65_DC0261E0-43C3-4027-9294-29100BAEAD07.PNG",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/bd0f61240e5db55b_58691502-7B71-4C5D-B600-7B28F8129349.PNG",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/880959a11b5f6319_97D37365-C7EC-40AC-BE7A-7F9F517EC66C.PNG",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/dc4f1bedcfffc318_29AC46FB-73F9-4DD4-83CC-02A6FCCCC74E.PNG",
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_digital-monaco/artifacts/76b7f86353b3b70a_4AE3352D-4327-4295-86A2-82AAADF01EB1.PNG",
];
const ILONA_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_digital-monaco/artifacts/kv1uyups_1.JPEG";

const APropos = () => {
  const { t } = useT();
  const s1 = t("apropos.s1");
  const s2 = t("apropos.s2");
  const s3 = t("apropos.s3");
  const s4 = t("apropos.s4");

  return (
    <main data-testid="page-apropos" className="bg-white">
      <section className="pt-40 pb-12 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-5xl md:text-7xl text-[#1A1528] font-bold">
            {t("apropos.title")}
          </h1>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 relative">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <MarqueeImage images={[MONACO_SUNSET, GEOMETRIC, MONACO_AERIAL]} testId="apropos-marquee-1" />
          </Reveal>
          <Reveal delay={0.15} className="apropos-text" data-testid="apropos-text-1">
            {s1.map((p, i) => <p key={i}><RichText text={p} /></p>)}
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto flex flex-col gap-12">
          <Reveal>
            <MarqueeImage images={BAND2} testId="apropos-marquee-2" />
          </Reveal>
          <Reveal delay={0.15} className="apropos-text max-w-3xl mx-auto" data-testid="apropos-text-2">
            {s2.map((p, i) => <p key={i}><RichText text={p} /></p>)}
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <MarqueeImage images={BAND3} testId="apropos-marquee-3" />
          </Reveal>
          <Reveal delay={0.15} className="apropos-text" data-testid="apropos-text-3">
            {s3.map((p, i) => <p key={i}><RichText text={p} /></p>)}
            <p className="font-serif italic text-2xl text-[#6B5CE7] pt-3">{t("apropos.s3Quote")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal className="apropos-text order-2 lg:order-1" data-testid="apropos-text-4">
            {s4.map((p, i) => <p key={i}><RichText text={p} /></p>)}
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <div className="relative">
              <img
                src={ILONA_PORTRAIT}
                alt="Ilona"
                data-testid="ilona-portrait"
                className="w-full aspect-[4/5] object-cover object-bottom rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-5 left-6 bg-white px-5 py-3 rounded-full shadow-lg">
                <p className="font-serif text-sm text-[#1A1528]">
                  <strong>Ilona</strong> — {t("apropos.founderCaption")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 px-6 bg-transparent">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <p className="font-serif italic text-2xl md:text-4xl text-[#6B5CE7] leading-tight max-w-xl">
            {t("apropos.ctaBanner")}
          </p>
          <Link to="/offres" data-testid="apropos-cta-offres" className="btn-violet shrink-0">
            {t("common.decouvrir")}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default APropos;
